import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { Button } from 'shared/components';
import { GqlImageBanner, ImageBannerType } from 'types/graphql';
import { ScheduleForm } from 'src/components/schedule-form';
import { InlineBanner } from 'src/components/inline-banner';
import { ButtonContainer } from '../configuration-step/configuration-step.styles';

type ScheduleStepProps = {
  banner: GqlImageBanner | undefined;
  currentStepIndex: number;
  setStepNumber: (newStep: number) => void;
  setBannerData: (data: Partial<GqlImageBanner>) => void;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
};

const InheritedScheduleContainer = styled.div`
  padding: 20px 0;
`;

const StyledInlineBanner = styled(InlineBanner)`
  margin-bottom: 20px;
`;

const InheritedDescription = styled.p`
  color: #6d747b;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 20px;
`;

/**
 * Schedule step for image banner creation/editing
 * Wraps the ScheduleForm component to work with image banners
 * Shows an inherited schedule message when connected to a special
 */
export function ScheduleStep({
  banner,
  currentStepIndex,
  setStepNumber,
  setBannerData,
  setIsDirty,
}: ScheduleStepProps): JSX.Element {
  const bannerType = banner?.bannerType ?? ImageBannerType.link;
  const isSpecialBanner = bannerType === ImageBannerType.special;

  // Handle next button click for inherited schedule
  function handleNext(): void {
    setStepNumber(currentStepIndex + 2);
  }

  // If this is a special banner, show the inherited schedule message
  if (isSpecialBanner) {
    return (
      <InheritedScheduleContainer>
        <InheritedDescription>
          When connecting an image banner to a special, the banner will automatically follow the special's schedule.
        </InheritedDescription>
        <StyledInlineBanner
          severity='info'
          description='Schedule inherited from discount schedule. The image banner will be displayed when the connected special is active.'
        />
        <ButtonContainer>
          <Button onClick={handleNext}>Next</Button>
        </ButtonContainer>
      </InheritedScheduleContainer>
    );
  }

  // Adapt the collection-based ScheduleForm to work with image banners
  const handleSetScheduleData = ({ schedule }: { schedule: any }): void => {
    setBannerData({ schedule });
  };

  // Convert banner to collection-like structure for ScheduleForm
  const bannerAsCollection = banner
    ? ({
        schedule: banner.schedule,
      } as any)
    : undefined;

  return (
    <ScheduleForm
      collection={bannerAsCollection}
      setCollectionData={handleSetScheduleData}
      setIsDirty={setIsDirty}
      onContinue={() => setStepNumber(currentStepIndex + 2)}
    />
  );
}
